import { api } from '../lib/api';
import type { EntityDocument, EntityRevision, EntityVersion } from '../types/entities';

type VersionHistoryStore = {
  activeEntity: EntityDocument | null;
  saveState: string;
  loadEntityVersions(): Promise<void>;
  loadEntities(): Promise<void>;
  loadExplorer(): Promise<void>;
  loadTimeline(): Promise<void>;
  loadCharacterTimeline(): Promise<void>;
  loadBacklinks(entityId?: string): Promise<void>;
  scheduleStoryTimelineRefresh(): void;
  loadSceneSegments(sceneId?: string, entityVersionId?: string | null): Promise<void>;
  loadSceneConflicts(sceneId?: string): Promise<void>;
  refreshAfterEntityVersionChange(): Promise<void>;
};

const historyRequests = new WeakMap<EntityDocument, Promise<void>>();
const historySnapshotRequests = new WeakMap<EntityDocument, Map<string, Promise<EntityVersion | EntityRevision | undefined>>>();

function mergeEntityVersionContent(previous: EntityVersion[], fresh: EntityVersion[]) {
  return fresh.map((version) => {
    const storedVersion = previous.find((item) => item.id === version.id);
    const revisions = version.revisions.map((revision) => {
      const storedRevision = storedVersion?.revisions.find((item) => item.id === revision.id);
      return storedRevision?.contentLoaded
        ? { ...revision, contentLoaded: true, contentDoc: storedRevision.contentDoc, fieldValues: storedRevision.fieldValues }
        : revision;
    });
    return storedVersion?.contentLoaded
      ? { ...version, contentLoaded: true, contentDoc: storedVersion.contentDoc, fieldValues: storedVersion.fieldValues, revisions }
      : { ...version, revisions };
  });
}

export const workspaceVersionHistoryActions = {
  async setWritingStatus(this: VersionHistoryStore, isWritingDraft: boolean, expectedEntityId?: string) {
    const entity = this.activeEntity;
    if (!entity) return;
    if (expectedEntityId && entity.id !== expectedEntityId) throw new Error('Die Auswahl wurde inzwischen gewechselt. Öffne das gewünschte Kapitel oder die Szene erneut.');
    const updated = await api<EntityDocument>(`/api/v1/entities/${entity.id}/writing-status`, {
      method: 'PUT', body: JSON.stringify({ isWritingDraft, expectedDraft: entity.isWritingDraft ?? false, versionId: entity.workingVersion.id, baseRevision: entity.workingVersion.revision })
    });
    if (this.activeEntity?.id !== entity.id) {
      await Promise.all([this.loadEntities(), this.loadTimeline(), this.loadCharacterTimeline()]);
      return;
    }
    this.activeEntity = updated;
    await this.refreshAfterEntityVersionChange();
    await this.loadCharacterTimeline();
  },
  async loadEntityVersions(this: VersionHistoryStore) {
    const entity = this.activeEntity;
    if (!entity || entity.historyLoaded !== false) return;
    const pending = historyRequests.get(entity);
    if (pending) return pending;
    const request = (async () => {
      const response = await api<{ items: EntityDocument['versions'] }>(`/api/v1/entities/${entity.id}/versions`);
      // A response for an earlier visit must not update a newly opened tab.
      if (this.activeEntity !== entity) return;
      entity.versions = response.items;
      entity.historyLoaded = true;
      try {
        entity.changeToken = (await api<{ token: string }>(`/api/v1/entities/${entity.id}/change-state?includeHistory=true`)).token;
      } catch { entity.changeToken = undefined; }
    })();
    historyRequests.set(entity, request);
    try { await request; } finally { historyRequests.delete(entity); }
  },
  async loadEntityVersionSnapshot(this: VersionHistoryStore, versionId: string, revisionId: string | null = null) {
    const entity = this.activeEntity;
    if (!entity) return;
    const version = entity.versions.find((item) => item.id === versionId);
    const target = revisionId ? version?.revisions.find((item) => item.id === revisionId) : version;
    if (!target || target.contentLoaded) return target;
    let requests = historySnapshotRequests.get(entity);
    if (!requests) { requests = new Map(); historySnapshotRequests.set(entity, requests); }
    const key = `${versionId}:${revisionId ?? 'current'}`;
    const pending = requests.get(key);
    if (pending) return pending;
    const request = (async () => {
      const query = revisionId ? `?revisionId=${encodeURIComponent(revisionId)}` : '';
      const response = await api<{ kind: 'version' | 'revision'; item: EntityVersion | EntityRevision }>(`/api/v1/entity-versions/${versionId}/snapshot${query}`);
      if (this.activeEntity !== entity) return;
      Object.assign(target, response.item);
      return target;
    })();
    requests.set(key, request);
    try { return await request; } finally { requests.delete(key); }
  },
  async refreshActiveVersionMetadata(this: VersionHistoryStore, entityId?: string) {
    const id = entityId ?? this.activeEntity?.id;
    if (!id) return;
    const entity = this.activeEntity;
    const includeHistory = entity?.historyLoaded !== false;
    const fresh = await api<EntityDocument>(`/api/v1/entities/${id}${includeHistory ? '' : '?includeHistory=false'}`);
    if (this.activeEntity !== entity || this.activeEntity?.id !== id) return;
    this.activeEntity.workingVersion.revision = fresh.workingVersion.revision;
    this.activeEntity.workingVersion.updatedAt = fresh.workingVersion.updatedAt;
    this.activeEntity.workingVersion.contentRevision = fresh.workingVersion.contentRevision;
    this.activeEntity.workingVersion.lastCheckpointRevision = fresh.workingVersion.lastCheckpointRevision;
    if (includeHistory) this.activeEntity.versions = mergeEntityVersionContent(this.activeEntity.versions, fresh.versions);
    else if (this.activeEntity.historyLoaded) {
      // The inspector may have opened while the metadata request was running.
      this.activeEntity.historyLoaded = false;
      await this.loadEntityVersions();
    }
  },
  async createEntityVersion(this: VersionHistoryStore, input: { sourceVersionId?: string | null; sourceRevisionId?: string | null; name?: string } = {}) {
    if (!this.activeEntity) return;
    this.activeEntity = await api<EntityDocument>(`/api/v1/entities/${this.activeEntity.id}/versions`, {
      method: 'POST', body: JSON.stringify(input)
    });
    await this.refreshAfterEntityVersionChange();
    return this.activeEntity;
  },
  async renameEntityVersion(this: VersionHistoryStore, versionId: string, name: string) {
    if (!this.activeEntity) return;
    await api(`/api/v1/entity-versions/${versionId}`, { method: 'PATCH', body: JSON.stringify({ name }) });
    this.activeEntity = await api<EntityDocument>(`/api/v1/entities/${this.activeEntity.id}`);
    return this.activeEntity;
  },
  async activateEntityVersion(this: VersionHistoryStore, versionId: string) {
    this.activeEntity = await api<EntityDocument>(`/api/v1/entity-versions/${versionId}/activate`, { method: 'POST' });
    await this.refreshAfterEntityVersionChange();
    return this.activeEntity;
  },
  async archiveEntityVersion(this: VersionHistoryStore, versionId: string) {
    this.activeEntity = await api<EntityDocument>(`/api/v1/entity-versions/${versionId}/archive`, { method: 'POST' });
    await this.refreshAfterEntityVersionChange();
    return this.activeEntity;
  },
  async deleteEntityVersion(this: VersionHistoryStore, versionId: string) {
    this.activeEntity = await api<EntityDocument>(`/api/v1/entity-versions/${versionId}`, { method: 'DELETE' });
    await this.refreshAfterEntityVersionChange();
    return this.activeEntity;
  },
  async compareEntityVersions(this: VersionHistoryStore, leftVersionId: string, rightVersionId: string) {
    if (!this.activeEntity) throw new Error('Keine Entity geöffnet.');
    return api<{ analysis: string; model: 'gpt-5.6-luna'; leftVersionId: string; rightVersionId: string; usage: { inputTokens: number; outputTokens: number; totalTokens: number } | null }>(`/api/v1/entities/${this.activeEntity.id}/versions/compare`, {
      method: 'POST', body: JSON.stringify({ leftVersionId, rightVersionId })
    });
  },
  async refreshAfterEntityVersionChange(this: VersionHistoryStore) {
    if (!this.activeEntity) return;
    const id = this.activeEntity.id;
    await Promise.all([this.loadEntities(), this.loadExplorer(), this.loadTimeline(), this.loadBacklinks(id)]);
    if (this.activeEntity.templateCategory === 'szene' || this.activeEntity.templateCategory === 'kapitel') this.scheduleStoryTimelineRefresh();
    if (this.activeEntity.templateCategory === 'szene') {
      await this.loadSceneSegments(id, this.activeEntity.workingVersion.id);
      await this.loadSceneConflicts(id);
    }
    this.saveState = 'clean';
  }
};
